/**
 * Spec 010 US1 (T034) — `slot.cloud.*` localStorage helpers.
 *
 * Stores the user-supplied URL + bearer token + the hardware label
 * the server reported on the last successful probe. The CloudGpuModal
 * (T036) calls `setRemoteConfig` after a successful `GET /v1/health`
 * probe; the EngineBadge (T037) reads the hardware label for display.
 *
 * Keys live under the `slot.cloud.*` namespace. The token is held in
 * localStorage by design — the spec 010 threat model treats the
 * dashboard as a designer-trusted tool, and the alternative (typing
 * the token on every dashboard load) is worse UX without a real
 * security gain on a designer's own machine.
 */

const STORAGE_KEY_URL = "slot.cloud.url";
const STORAGE_KEY_TOKEN = "slot.cloud.token";
const STORAGE_KEY_HARDWARE_LABEL = "slot.cloud.hardware_label";

export interface RemoteConfig {
  url: string;
  token: string;
  hardwareLabel?: string;
}

/**
 * Read the persisted Cloud GPU connection settings. Returns
 * `undefined` if either the URL or the token is missing — both are
 * required to construct a `RemoteEngineAdapter`.
 *
 * The `hardwareLabel` field is optional and only set after the first
 * successful `GET /v1/info` probe; older sessions / fresh
 * configurations will return without it.
 */
export function getRemoteConfig(): RemoteConfig | undefined {
  if (typeof localStorage === "undefined") {
    return undefined;
  }
  const url = localStorage.getItem(STORAGE_KEY_URL);
  const token = localStorage.getItem(STORAGE_KEY_TOKEN);
  if (url === null || url === "" || token === null || token === "") {
    return undefined;
  }
  const hardwareLabel = localStorage.getItem(STORAGE_KEY_HARDWARE_LABEL);
  const config: RemoteConfig = { url, token };
  if (hardwareLabel !== null && hardwareLabel !== "") {
    config.hardwareLabel = hardwareLabel;
  }
  return config;
}

/**
 * Persist Cloud GPU connection settings. Called by `CloudGpuModal`
 * after the user supplies + validates the URL + token, and by
 * `RemoteEngineAdapter` after each successful info probe (to refresh
 * the cached hardware label).
 *
 * Empty strings are rejected (would round-trip as "missing" via
 * `getRemoteConfig`); pass via `clearRemoteConfig` to remove.
 */
export function setRemoteConfig(config: RemoteConfig): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  if (config.url === "" || config.token === "") {
    throw new Error("setRemoteConfig: url and token must be non-empty");
  }
  localStorage.setItem(STORAGE_KEY_URL, config.url);
  localStorage.setItem(STORAGE_KEY_TOKEN, config.token);
  // `getRemoteConfig` treats an empty / missing hardware-label key
  // as absent, so persist the same way on the write path — otherwise
  // `set({hardwareLabel: ""})` → `get()` drops the value silently
  // (CR catch).
  if (config.hardwareLabel !== undefined && config.hardwareLabel !== "") {
    localStorage.setItem(STORAGE_KEY_HARDWARE_LABEL, config.hardwareLabel);
  } else {
    localStorage.removeItem(STORAGE_KEY_HARDWARE_LABEL);
  }
}

/**
 * Remove all `slot.cloud.*` keys. Called when the user explicitly
 * disconnects Cloud GPU from the dashboard (e.g., to switch tokens
 * after rotation, or to fall back to a local adapter).
 */
export function clearRemoteConfig(): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.removeItem(STORAGE_KEY_URL);
  localStorage.removeItem(STORAGE_KEY_TOKEN);
  localStorage.removeItem(STORAGE_KEY_HARDWARE_LABEL);
}
